/**
 * Converts a column index (0-based) to a letter label: 0 → A, 1 → B, …, 25 → Z, 26 → AA, …
 *
 * @param {number} index
 * @returns {string}
 */
export function colLabel( index ) {
	let label = '';
	let n = index;
	do {
		label = String.fromCharCode( 65 + ( n % 26 ) ) + label;
		n = Math.floor( n / 26 ) - 1;
	} while ( n >= 0 );
	return label;
}

/**
 * Converts a column label (e.g. "A", "Z", "AA", "AX") to a 0-based index.
 *
 * @param {string} label
 * @returns {number}
 */
export function colIndex( label ) {
	let n = 0;
	for ( let i = 0; i < label.length; i++ ) {
		n = n * 26 + ( label.charCodeAt( i ) - 64 );
	}
	return n - 1;
}

/**
 * Parse a range expression token such as:
 *   "A1"       → single tile
 *   "A1-20"    → col A, rows 1–20
 *   "A-C10"    → cols A–C, row 10
 *   "G-I15-20" → cols G–I, rows 15–20
 *
 * Returns a flat string[] of tile IDs.
 *
 * @param {string} token - Trimmed range token (no commas).
 * @returns {string[]}
 */
function parseToken( token ) {
	// Match: (col or col-col)(row or row-row)
	// Col part: one or more uppercase letters, optionally followed by dash + more letters
	// Row part: one or more digits, optionally followed by dash + more digits
	const match = token.match( /^([A-Z]+(?:-[A-Z]+)?)(\d+(?:-\d+)?)$/ );
	if ( ! match ) return [];

	const colPart = match[ 1 ];
	const rowPart = match[ 2 ];

	// Parse col range
	let colStart, colEnd;
	if ( colPart.includes( '-' ) ) {
		const [ cStart, cEnd ] = colPart.split( '-' );
		colStart = colIndex( cStart );
		colEnd   = colIndex( cEnd );
	} else {
		colStart = colEnd = colIndex( colPart );
	}

	// Parse row range
	let rowStart, rowEnd;
	if ( rowPart.includes( '-' ) ) {
		const [ rStart, rEnd ] = rowPart.split( '-' );
		rowStart = parseInt( rStart, 10 );
		rowEnd   = parseInt( rEnd, 10 );
	} else {
		rowStart = rowEnd = parseInt( rowPart, 10 );
	}

	const ids = [];
	for ( let c = colStart; c <= colEnd; c++ ) {
		for ( let r = rowStart; r <= rowEnd; r++ ) {
			ids.push( colLabel( c ) + r );
		}
	}
	return ids;
}

/**
 * Parse a range expression string (comma-separated tokens) or an array of tile IDs.
 * Returns a flat string[] of tile IDs (deduped, preserving order of first occurrence).
 *
 * @param {string|string[]} expr
 * @returns {string[]}
 */
export function parseTileRange( expr ) {
	if ( Array.isArray( expr ) ) {
		// Already an array of IDs — return as-is (deduped)
		const seen = new Set();
		return expr.filter( ( id ) => {
			if ( seen.has( id ) ) return false;
			seen.add( id );
			return true;
		} );
	}

	if ( typeof expr !== 'string' || ! expr.trim() ) return [];

	const seen = new Set();
	const result = [];

	for ( const token of expr.split( ',' ) ) {
		const t = token.trim().toUpperCase();
		if ( ! t ) continue;
		for ( const id of parseToken( t ) ) {
			if ( ! seen.has( id ) ) {
				seen.add( id );
				result.push( id );
			}
		}
	}

	return result;
}

/**
 * Normalises a raw tile ID value (string range expression, array of IDs, or
 * undefined/null/empty) to a flat string[].
 *
 * @param {string|string[]|null|undefined} raw
 * @returns {string[]}
 */
export function normalizeTileIds( raw ) {
	if ( ! raw ) return [];
	return parseTileRange( raw );
}
